<script setup lang="ts">
import type { IconsKeys } from '../icon/types'
import { SnackbarLocation, SnackbarType } from './types'

type SettingsValues = {
  icon: IconsKeys
}

type Settings = Record<SnackbarType, SettingsValues>

type Props = {
  message: string
  type?: SnackbarType
  location?: SnackbarLocation
  timeout?: number
}

const props = withDefaults(defineProps<Props>(), {
  type: SnackbarType.MESSAGE,
  location: SnackbarLocation.TOP,
  timeout: 3000,
})

const model = defineModel<boolean>()

const isVisible = ref(model.value)

watch(model, (newValue) => {
  isVisible.value = newValue

  if (newValue) startTimeout()
})

const settings: Settings = {
  [SnackbarType.MESSAGE]: {
    icon: 'alert-circle',
  },
  [SnackbarType.SUCCESS]: {
    icon: 'checkmark-circle',
  },
}

const startTimeout = () => {
  setTimeout(() => {
    isVisible.value = false
    model.value = false
  }, props.timeout)
}

onMounted(() => {
  if (isVisible.value) startTimeout()
})

const snackbarClasses = computed(() => {
  return [
    'app-snackbar',
    `app-snackbar--location-${props.location}`,
    `app-snackbar--type-${props.type}`,
  ]
})
</script>

<template>
  <Teleport to="body">
    <transition name="ui-scale">
      <div
        v-if="isVisible"
        data-test-id="snackbar"
        :class="snackbarClasses"
        v-bind="$attrs"
      >
        <div class="app-snackbar__content">
          <the-icon
            class="app-snackbar__icon"
            color="var(--ui-white)"
            :name="settings[type].icon"
          />

          <div class="app-snackbar__message">{{ message }}</div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-snackbar {
  position: fixed;
  right: 0;
  left: 0;
  z-index: 10001;
  min-width: 335px;
  max-width: 335px;
  min-height: 40px;
  margin: 0 auto;
  background-color: var(--ui-secondary);
  border-radius: 10px;

  @include media('>=desktop-mini') {
    max-width: 350px;
  }

  &__content {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 16px;
    margin-right: auto;
    font-weight: 400;
    text-align: initial;
  }

  &__icon {
    flex-shrink: 0;
    align-self: flex-start;
    width: 24px;
    height: 24px;
  }

  &__message {
    font-size: 14px;
    font-weight: 600;
    -webkit-hyphens: auto;
    hyphens: auto;
    line-height: 19px;
    color: var(--ui-white);
  }

  &--location-top {
    top: 60px;

    @include media('>=tablet') {
      top: 134px;
    }
  }

  &--location-bottom {
    bottom: 8px;
  }

  &--type-success {
    background-color: var(--ui-green);
  }
}
</style>
